import * as yup from 'yup'
import {
  AddressSchema,
  createNewAddressRequest,
  INewAddress,
  IExistingFacilityAddress,
  createUpdateAddressRequest,
} from 'apps/fleet-planner/schema/AddressSchema'
import {
  ContactSchema,
  IExistingContact,
  INewContact,
} from 'apps/fleet-planner/schema/ContactSchema'
import * as Types from 'apps/fleet-planner/types'
import {
  FacilityPurpose,
  IFacility,
  IUpdateFacilityRequest,
} from 'apps/fleet-planner/utils/api/facilities'

const requiredMessage = 'This field is required.'

export interface INewFacility extends Pick<IFacility, 'name'> {
  address: INewAddress
  contact: INewContact
}

export interface IExistingFacility extends Pick<IFacility, 'name'> {
  id: string
  address: IExistingFacilityAddress
  contact: IExistingContact
}

export /**
 * Creates a new facility
 * @note This model is not a 1-to-1 mapping from API model. `address` and `contact`
 * are included here as an object instead of an array to make it easier to edit
 * as a single object.
 * @see https://fp-staging.transfix.io/api-docs/index.html#/Facilities/post_facilities
 */
const FacilitySchema = yup.object<INewFacility>({
  address: AddressSchema,
  contact: ContactSchema,
  name: yup
    .mixed()
    .required(requiredMessage)
    /**
     * This validation tests that the value passed is a "new" facility name
     * except removes the false negative case where it's the same as it's original value
     */
    .test('duplicate-facility', 'This facility name is already in use.', function duplicateFacility(
      val: string
    ) {
      const originalFacilityName = (this.options?.context as any)?.originalFacilityName || ''
      const facilities: IFacility[] = (this.options?.context as any)?.facilities || []

      // Filters out values that match the original name, in cases of updating
      const isNewFacilityName = !facilities
        .filter(f => f?.name?.toLowerCase() !== originalFacilityName?.toLowerCase())
        .find(f => {
          return f?.name?.toLowerCase() === val?.toLowerCase()
        })

      return isNewFacilityName
    }),
})

export const ExistingFacilitySchema = yup.object<IExistingFacility>({
  id: yup.string().required(),
  address: yup.object(), // TODO: use schema
  contact: yup.object(), // TODO: use ConcatSchema
  name: yup
    .mixed()
    .required(requiredMessage)
    /**
     * This validation tests that the value passed is a "new" facility name
     * except removes the false negative case where it's the same as it's original value
     */
    .test('duplicate-facility', 'This facility name is already in use.', function duplicateFacility(
      val: string
    ) {
      const originalFacilityName = (this.options?.context as any)?.originalFacilityName || ''
      const facilities: IFacility[] = (this.options?.context as any)?.facilities || []

      // Filters out values that match the original name, in cases of updating
      const isNewFacilityName = !facilities
        .filter(f => f?.name?.toLowerCase() !== originalFacilityName?.toLowerCase())
        .find(f => {
          return f?.name?.toLowerCase() === val?.toLowerCase()
        })

      return isNewFacilityName
    }),
})

export type FacilitySchemaType = yup.InferType<typeof FacilitySchema>
export type ExistingFacilitySchemaType = yup.InferType<typeof ExistingFacilitySchema>

export type INewFacilityRequest = Omit<IFacility, 'id'>
/**
 * Creates a new facility request object for POST /facilities
 * @param {FacilitySchemaType} data - Form output
 */
export const createNewFacilityRequest = (
  data: FacilitySchemaType,
  overrides: Partial<INewFacilityRequest> = {}
): INewFacilityRequest => {
  const { address: addressObj, contact, name = '' } = data || {}
  const address = createNewAddressRequest(addressObj, { addressee: name })

  // If contact is valid...
  const contacts: Types.ICustomerContact[] = []
  if (contact.name || contact.email || contact.phone || contact.phoneExt) {
    contacts.push({
      ...contact,
      purpose: Types.CUSTOMER_CONTACT_PURPOSE.FACILITY_PRIMARY,
    })
  }

  return {
    name,
    contacts,
    addresses: [
      {
        ...address,
        purpose: FacilityPurpose.facilityPrimary,
      },
    ],
    ...overrides,
  }
}

/**
 * Creates a new update facility request for PUT /facilties
 * @param data
 * @param overrides
 */
export const createUpdateFacilityRequest = (
  data: ExistingFacilitySchemaType,
  overrides: Partial<IFacility> = {}
): IUpdateFacilityRequest => {
  const { address: addressObj, contact, id, name = '' } = data || {}

  const address = createUpdateAddressRequest(addressObj, { addressee: name })
  const contacts: Types.ICustomerContact[] = []
  if (contact.name || contact.email || contact.phone || contact.phoneExt) {
    contacts.push({
      ...contact,
      purpose: Types.CUSTOMER_CONTACT_PURPOSE.FACILITY_PRIMARY,
    })
  }

  return {
    facility: {
      id,
      name,
      contacts,
      addresses: [
        {
          ...address,
          purpose: FacilityPurpose.facilityPrimary,
        },
      ],
      ...overrides,
    },
  }
}
